import { Form, Formik } from 'formik';
import camelCase from 'lodash/camelCase';
import isEqual from 'lodash/isEqual';
import snakeCase from 'lodash/snakeCase';
import React, { useEffect, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { useWindowSize } from 'usehooks-ts';

import { PrimaryButton, SecondaryButton } from '@components/Button/variants';
import EnableProfileModal from '@components/EnableProfileModal';
import TextAreaField from '@components/Form/TextAreaField';
import Spinner from '@components/Spinner';
import { useAppContext } from '@contexts/AppContext';
import { useDirectoryFilterOptions, useProfile, useSubscriberDirectory } from '@hooks';
import { edit_profile_settings_path, insights_path, subscriber_profile_path } from '@routes';
import {
  DirectoryForm,
  FilterOptionsResponse,
  SanitizedSearchParams,
} from '@utils/backend-api/subscriber_directory';

import FilterNav from './FilterNav';
import Subscribers from './Subscribers';

export const INITIAL_FORM_VALUES: DirectoryForm = {
  openTo: [],
  aiQuery: '',
};

export interface ShowFilterMap {
  openTo: boolean;
}

const INITIAL_SHOW_FILTERS_STATE: ShowFilterMap = {
  openTo: true,
};

// converts search params to two objects, one for backend and one for form:
// [{query_one: val, another: [val2a, val2b]}, {queryOne: val, another: [val2a, val2b]}]
// example decoded search params: ?open_to=hiring||finding_new_job&company=google&location=new york city
const sanitizeSearchParams = (searchParams) => {
  const sanitizedSearchParams = {};
  const formSearchParams = {};
  for (const [key, value] of searchParams) {
    const decodedValue = decodeURIComponent(value.toLowerCase());
    // if key in initial state is an array, then value should be converted to array
    sanitizedSearchParams[snakeCase(key)] = Array.isArray(INITIAL_FORM_VALUES[camelCase(key)])
      ? decodedValue.split('||')
      : decodedValue;
    formSearchParams[camelCase(key)] = Array.isArray(INITIAL_FORM_VALUES[camelCase(key)])
      ? decodedValue.split('||')
      : decodedValue;
  }
  return [sanitizedSearchParams, formSearchParams];
};

const DirectoryContent = () => {
  const [formValues, setFormValues] = useState<DirectoryForm>(INITIAL_FORM_VALUES);
  const [showSlideFilter, setShowSlideFilter] = useState<boolean>(false);
  const [showFilterMap, setShowFilterMap] = useState<ShowFilterMap>(INITIAL_SHOW_FILTERS_STATE);
  const [displayedFilterOptions, setDisplayedFilterOptions] = useState<FilterOptionsResponse>();
  const { width } = useWindowSize();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile } = useProfile();
  const [sanitizedSearchParams, formSearchParams] = useMemo(
    () => sanitizeSearchParams(searchParams),
    [searchParams]
  );
  const { filterOptions, areFilterOptionsLoading } =
    useDirectoryFilterOptions(sanitizedSearchParams);
  const { subscribersData, isLoadingSubscriberDirectory } =
    useSubscriberDirectory(sanitizedSearchParams);
  const { isWebview } = useAppContext();

  useEffect(() => {
    if (!isLoadingSubscriberDirectory) {
      setFormValues({ ...INITIAL_FORM_VALUES, ...formSearchParams });

      // show filters as open on page load if passed in query params
      const onLoadShowFilterMap = Object.fromEntries(
        Object.entries(formSearchParams).map(([param, value]) => [
          param,
          !isEqual(value, INITIAL_FORM_VALUES[param]),
        ])
      );
      setShowFilterMap((prevState) => ({
        ...prevState,
        ...onLoadShowFilterMap,
      }));
    }
  }, [isLoadingSubscriberDirectory, formSearchParams]);

  useEffect(() => {
    if (!areFilterOptionsLoading) {
      setDisplayedFilterOptions(filterOptions);
    }
  }, [areFilterOptionsLoading, filterOptions]);

  useEffect(() => {
    if (width > 768) {
      setShowSlideFilter(false);
      setShowFilterMap((prevState) => ({ ...prevState, openTo: true }));
    } else {
      setShowFilterMap((prevState) => ({ ...prevState, openTo: false }));
    }
  }, [width]);

  const updateSearchParams = (sanitizedParams: SanitizedSearchParams) => {
    const updatedSearchParams = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(sanitizedParams)) {
      if (!value || isEqual(value, INITIAL_FORM_VALUES[camelCase(key)])) {
        updatedSearchParams.delete(key);
      } else {
        const queryStringVal = encodeURIComponent(Array.isArray(value) ? value.join('||') : value);
        updatedSearchParams.set(key, queryStringVal);
      }
    }
    setSearchParams(updatedSearchParams, { replace: true });
  };

  const handleSubmit = async (values: DirectoryForm) => {
    const sanitizedParams: SanitizedSearchParams = {
      open_to: values.openTo,
      ai_query: values.aiQuery,
    };

    updateSearchParams(sanitizedParams);
  };

  return (
    <div id="SubscriberDirectory">
      {!displayedFilterOptions ? (
        <Spinner />
      ) : (
        <Container>
          {!subscribersData?.canViewDirectory && !isLoadingSubscriberDirectory && (
            <EnableProfileModal
              isLoggedIn={true}
              isSubscriber={subscribersData.currentUserStatus > 1}
            />
          )}
          {!isWebview ? (
            <>
              <Row className="header-container">
                <Col className="col-sm-12 col-md-7">
                  <h3 className="header-text">Subscriber Directory</h3>
                  <div className="subheader-text">
                    Make connections worth your time without all the noise. Search, find and engage
                    with others who are serious about tech and business. See{' '}
                    <a
                      href={insights_path()}
                      className="subheader-text-link hover:text-primary-magenta focus:text-primary-magenta"
                    >
                      insights
                    </a>{' '}
                    about the subscriber base.
                  </div>
                </Col>
                <Col className="col-sm-12 col-md-5 buttons-header-wrapper btn-container text-center">
                  <div className="buttons-wrapper w-[150px]">
                    <PrimaryButton
                      as="button"
                      className="mb-16"
                      onClick={() => (window.location = subscriber_profile_path(profile.username))}
                      size="small"
                      fullWidth
                    >
                      View my profile
                    </PrimaryButton>
                    <SecondaryButton
                      as="button"
                      onClick={() => (window.location = edit_profile_settings_path())}
                      size="small"
                      fullWidth
                    >
                      Update my profile
                    </SecondaryButton>
                  </div>
                </Col>
              </Row>
              <Row className="divider">
                <div className="col-sm-12">
                  <hr />
                </div>
              </Row>
            </>
          ) : (
            <div className="mt2" />
          )}

          <Formik initialValues={formValues} enableReinitialize={true} onSubmit={handleSubmit}>
            {(formik) => (
              <Form onSubmit={formik.handleSubmit}>
                <Row className="filters-section-container">
                  <Col
                    className={`col-sm-12 col-md-4 filters-section ${
                      showSlideFilter ? 'show' : ''
                    }`}
                  >
                    <FilterNav
                      {...displayedFilterOptions}
                      showSlideFilter={showSlideFilter}
                      setShowSlideFilter={setShowSlideFilter}
                      showFilterMap={showFilterMap}
                      setShowFilterMap={setShowFilterMap}
                      setDisplayedFilterOptions={setDisplayedFilterOptions}
                      handleResetForm={() => formik.setValues(INITIAL_FORM_VALUES)}
                    />
                  </Col>
                  <Col className="col-sm-12 col-md-8 subscribers-section">
                    {/* hidden button prevents the first button in the first subscriber list from
                    being 'clicked' when user hits enter button. Hacky but nothing else worked */}
                    <button style={{ display: 'none' }}></button>
                    <div className="search-container">
                      <div className="search-section">
                        <div className="search-header-container mb-8">
                          <div>
                            <i className="fa fa-search search-toggler" />
                          </div>
                          <div className="search-container-text">
                            AI directory search by name, industry, company, interest, etc.
                          </div>
                        </div>
                        <TextAreaField className="mb-24" name="aiQuery" />
                        <div className="btn-search-container">
                          <PrimaryButton as="button" type="submit">
                            Search
                          </PrimaryButton>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`list-section ${
                        isLoadingSubscriberDirectory ? 'top-center' : ''
                      } ${showSlideFilter ? 'hide' : ''}`}
                    >
                      {isLoadingSubscriberDirectory ? (
                        <Spinner />
                      ) : (
                        <Subscribers
                          profiles={subscribersData?.profiles || []}
                          isLoadingSubscriberDirectory={isLoadingSubscriberDirectory}
                          currentUserId={subscribersData?.currentUserId}
                          descriptionField={
                            formik.values.openTo.length === 1 && formik.values.openTo[0]
                          }
                        />
                      )}
                    </div>
                  </Col>
                </Row>
                {!isWebview && (
                  <Row>
                    <Col className="col-sm-12 col-md-4 mobile-nav-btn">
                      <SecondaryButton
                        as="button"
                        className="mt-48"
                        onClick={() => (window.location = edit_profile_settings_path())}
                      >
                        Update my profile
                      </SecondaryButton>
                    </Col>
                  </Row>
                )}
              </Form>
            )}
          </Formik>
        </Container>
      )}
    </div>
  );
};

export default DirectoryContent;
